import type { EffectiveIntakeSchema, FormConfigVersion } from "../form-config";
import { createDefaultIntakeData, LOCAL_STORAGE_KEY } from "../form-schema";
import type { DraftState, FileCategory, UploadedFile } from "../types";
import { loadStoredDraft, normalizeDraftShape, nowIso } from "./draft-state";
import { createSubmissionPresentationManifest } from "./submission-document";
import { PartnerUploadError } from "./upload-failure";
import { fieldValueToString } from "./value-utils";

export type PartnerInviteStatus = "DRAFT" | "REVISION_REQUESTED";

export type PartnerInvite = {
  id: string;
  draftId: string;
  categoryId: string | null;
  data: Record<string, unknown>;
  files: unknown[];
  expiresAt: string;
  updatedAt: string;
  status?: PartnerInviteStatus;
  draftVersion?: number;
  currentStep?: number;
  revisionReason?: string | null;
  hasSavedAdminPassword?: boolean;
};

export type InviteFetchResult =
  | { status: "success"; invite: PartnerInvite }
  | { status: "notFound" }
  | { status: "expired" }
  | { status: "submitted"; submittedAt?: string }
  | { status: "error"; message: string };

export type PartnerAutosaveResult =
  | { status: "success"; draftVersion: number; updatedAt: string; hasSavedAdminPassword: boolean }
  | { status: "expired" }
  | { status: "submitted" }
  | { status: "conflict" }
  | { status: "error"; message: string };

export type PartnerServerBase = {
  draftVersion: number;
  data: DraftState["data"];
  files: UploadedFile[];
};

export type PartnerDraftSyncResult =
  | { status: "unchanged"; draftVersion: number }
  | { status: "changed"; invite: PartnerInvite }
  | { status: "notFound" }
  | { status: "expired" }
  | { status: "submitted" }
  | { status: "error"; message: string };

const INVITE_PATH_PATTERN = /^\/invite\/([^/?#]+)\/?$/;
const DRAFT_CAPABILITY_STORAGE_PREFIX = "meet-the-world:draft-capability:";
const DRAFT_CAPABILITY_CONFLICT_MESSAGE = "Draft capability already exists";
const SERVER_BASE_STORAGE_PREFIX = "meet-the-world:server-base:";

export function getInviteIdFromPathname(pathname: string): string | null {
  const match = pathname.match(INVITE_PATH_PATTERN);
  return match?.[1] ?? null;
}

async function parseJson<T>(response: Response): Promise<T | null> {
  return (await response.json().catch(() => null)) as T | null;
}

function normalizePartnerInvite(candidate: Partial<PartnerInvite> | undefined | null): PartnerInvite | null {
  if (!candidate?.id || !candidate.draftId || !candidate.data) return null;
  return {
    id: candidate.id,
    draftId: candidate.draftId,
    categoryId: candidate.categoryId ?? null,
    data: candidate.data,
    files: Array.isArray(candidate.files) ? candidate.files : [],
    expiresAt: candidate.expiresAt ?? "",
    updatedAt: candidate.updatedAt ?? nowIso(),
    status: candidate.status === "REVISION_REQUESTED" ? "REVISION_REQUESTED" : "DRAFT",
    draftVersion: Number.isInteger(candidate.draftVersion) ? candidate.draftVersion! : 0,
    currentStep: Number.isInteger(candidate.currentStep) ? candidate.currentStep! : 0,
    revisionReason: typeof candidate.revisionReason === "string" ? candidate.revisionReason : null,
    hasSavedAdminPassword: candidate.hasSavedAdminPassword === true,
  };
}

export async function fetchPartnerInvite(inviteId: string): Promise<InviteFetchResult> {
  try {
    const response = await fetch(`/api/invites/${encodeURIComponent(inviteId)}`, { headers: { accept: "application/json" } });
    const body = await parseJson<{ invite?: Partial<PartnerInvite>; error?: string; status?: string; submittedAt?: string }>(response);
    if (response.status === 404) return { status: "notFound" };
    if (response.status === 410) return { status: "expired" };
    if (response.status === 409) return { status: "submitted", submittedAt: body?.submittedAt };
    const invite = normalizePartnerInvite(body?.invite);
    if (!response.ok || !invite) return { status: "error", message: body?.error ?? "초대 정보를 불러오지 못했습니다." };
    return { status: "success", invite };
  } catch (error) {
    return { status: "error", message: error instanceof Error ? error.message : "초대 정보를 불러오지 못했습니다." };
  }
}

export function createDraftFromInvite(invite: PartnerInvite, options: { currentStep?: number; config?: FormConfigVersion } = {}): DraftState | null {
  const now = nowIso();
  return normalizeDraftShape(
    {
      draftId: invite.draftId,
      inviteId: invite.id,
      draftVersion: invite.draftVersion,
      revisionReason: invite.revisionReason,
      hasSavedAdminPassword: invite.hasSavedAdminPassword,
      data: invite.data as DraftState["data"],
      files: Array.isArray(invite.files) ? (invite.files as UploadedFile[]) : [],
      currentStep: options.currentStep ?? invite.currentStep ?? 0,
      createdAt: now,
      updatedAt: invite.updatedAt || now,
    },
    options.config,
  );
}

export function hasMeaningfulInviteLocalDraft(candidate: DraftState | null): candidate is DraftState {
  return Boolean(
    candidate &&
    !candidate.submittedAt &&
    (candidate.data.category.categoryId || candidate.data.company.storeNameKo.trim() || candidate.files.length > 0),
  );
}

function stripCategory(data: DraftState["data"]): DraftState["data"] {
  return { ...data, category: createDefaultIntakeData().category };
}

export function hasMeaningfulPartnerEdit(draft: DraftState, initialData: DraftState["data"] = createDefaultIntakeData()): boolean {
  if (draft.files.length > 0) return true;
  return JSON.stringify(stripCategory(draft.data)) !== JSON.stringify(stripCategory(initialData));
}

// currentStep은 브라우저별 커서일 뿐 공동 콘텐츠가 아니므로 fingerprint에서 제외한다.
export function partnerDraftFingerprint(draft: Pick<DraftState, "data" | "files">): string {
  return JSON.stringify({ data: draft.data, files: draft.files });
}

export function partnerServerBaseFromDraft(draft: Pick<DraftState, "draftVersion" | "data" | "files">): PartnerServerBase {
  return { draftVersion: draft.draftVersion ?? 0, data: draft.data, files: draft.files };
}

export function loadStoredPartnerServerBase(inviteId: string): PartnerServerBase | null {
  try {
    const raw = localStorage.getItem(`${SERVER_BASE_STORAGE_PREFIX}${inviteId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PartnerServerBase> | null;
    if (!parsed || typeof parsed !== "object" || !parsed.data) return null;
    return {
      draftVersion: Number.isInteger(parsed.draftVersion) ? parsed.draftVersion! : 0,
      data: parsed.data as DraftState["data"],
      files: Array.isArray(parsed.files) ? parsed.files : [],
    };
  } catch {
    return null;
  }
}

export function storePartnerServerBaseLocally(inviteId: string, base: PartnerServerBase): void {
  try {
    // 임시 비밀번호는 write-only 값이므로 로컬 기준본에는 저장하지 않는다.
    const sanitized: PartnerServerBase = { ...base, data: { ...base.data, reservation: { ...base.data.reservation, adminPassword: "" } } };
    localStorage.setItem(`${SERVER_BASE_STORAGE_PREFIX}${inviteId}`, JSON.stringify(sanitized));
  } catch {
    // 기준본 저장 실패는 병합 정밀도만 낮출 뿐 편집을 막지 않는다.
  }
}

export async function fetchPartnerDraftSync(inviteId: string, sinceVersion?: number): Promise<PartnerDraftSyncResult> {
  try {
    const query = sinceVersion != null ? `?sinceVersion=${sinceVersion}` : "";
    const response = await fetch(`/api/invites/${encodeURIComponent(inviteId)}/draft${query}`, { headers: { accept: "application/json" } });
    const body = await parseJson<{ invite?: Partial<PartnerInvite>; unchanged?: boolean; draftVersion?: number; error?: string; status?: string }>(response);
    if (response.status === 404) return { status: "notFound" };
    if (response.status === 410) return { status: "expired" };
    if (response.status === 409) return { status: "submitted" };
    if (!response.ok) return { status: "error", message: body?.error ?? "초안 동기화에 실패했습니다." };
    if (body?.unchanged) return { status: "unchanged", draftVersion: body.draftVersion ?? sinceVersion ?? 0 };
    const invite = normalizePartnerInvite(body?.invite);
    if (!invite) return { status: "error", message: body?.error ?? "초안 동기화에 실패했습니다." };
    return { status: "changed", invite };
  } catch (error) {
    return { status: "error", message: error instanceof Error ? error.message : "초안 동기화에 실패했습니다." };
  }
}

export function getPartnerDraftStorageKey(draftId: string, storedDraftId?: string): string {
  return storedDraftId && storedDraftId !== draftId ? `${LOCAL_STORAGE_KEY}:${draftId}` : LOCAL_STORAGE_KEY;
}

export function storePartnerDraftLocally(draft: DraftState): void {
  const storedDraftId = loadStoredDraft()?.draftId;
  localStorage.setItem(getPartnerDraftStorageKey(draft.draftId, storedDraftId), JSON.stringify(draft));
}

// 서버는 inviteId를 UUID로만 받으므로 randomUUID가 없는 브라우저에서도 UUID v4 형식을 지켜야 한다.
export function createBrowserInviteId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") crypto.getRandomValues(bytes);
  else for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function getOrCreatePartnerInviteId(draftId: string): string {
  const storageKey = `${DRAFT_CAPABILITY_STORAGE_PREFIX}${draftId}`;
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) return stored;
    const created = createBrowserInviteId();
    localStorage.setItem(storageKey, created);
    return created;
  } catch {
    return createBrowserInviteId();
  }
}

// 이 브라우저의 draft capability가 유실되면 같은 draftId로는 서버 초안을 다시 만들 수 없다.
export function isPartnerDraftCapabilityConflictError(error: unknown): boolean {
  return error instanceof Error && error.message === DRAFT_CAPABILITY_CONFLICT_MESSAGE;
}

export async function createPartnerDraft(draft: DraftState): Promise<PartnerInvite> {
  const response = await fetch("/api/drafts", {
    method: "POST",
    headers: { "Content-Type": "application/json", accept: "application/json" },
    body: JSON.stringify({
      inviteId: getOrCreatePartnerInviteId(draft.draftId),
      draftId: draft.draftId,
      currentStep: draft.currentStep,
      data: draft.data,
      files: draft.files,
    }),
  });
  const body = await parseJson<{ invite?: Partial<PartnerInvite>; error?: string }>(response);
  if (response.status === 409 && body?.error === "Draft capability already exists") throw new Error(DRAFT_CAPABILITY_CONFLICT_MESSAGE);
  const invite = normalizePartnerInvite(body?.invite);
  if (!response.ok || !invite) throw new Error(body?.error ?? "초대 초안을 만들지 못했습니다.");
  return invite;
}

export async function autosavePartnerDraft(draft: DraftState): Promise<PartnerAutosaveResult> {
  if (!draft.inviteId) return { status: "error", message: "초대 링크 정보가 없습니다." };
  try {
    const response = await fetch(`/api/invites/${encodeURIComponent(draft.inviteId)}/draft`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", accept: "application/json" },
      body: JSON.stringify({
        draftVersion: draft.draftVersion ?? 0,
        currentStep: draft.currentStep,
        data: draft.data,
        files: draft.files,
      }),
    });
    const body = await parseJson<{ draft?: { draftVersion: number; updatedAt: string; hasSavedAdminPassword: boolean }; error?: string; status?: string }>(response);
    if (response.status === 410) return { status: "expired" };
    if (response.status === 409) return body?.status === "SUBMITTED" ? { status: "submitted" } : { status: "conflict" };
    if (!response.ok || !body?.draft) return { status: "error", message: body?.error ?? "자동 저장에 실패했습니다." };
    return { status: "success", draftVersion: body.draft.draftVersion, updatedAt: body.draft.updatedAt, hasSavedAdminPassword: body.draft.hasSavedAdminPassword };
  } catch (error) {
    return { status: "error", message: error instanceof Error ? error.message : "자동 저장에 실패했습니다." };
  }
}

export async function submitPartnerDraft(draft: DraftState, normalizedData: DraftState["data"], schema: Pick<EffectiveIntakeSchema, "configVersionId" | "schemaHash" | "steps">) {
  const response = await fetch("/api/submissions", {
    method: "POST",
    headers: { "Content-Type": "application/json", accept: "application/json" },
    body: JSON.stringify({
      draftId: draft.draftId,
      inviteId: draft.inviteId,
      draftVersion: draft.draftVersion,
      data: {
        ...normalizedData,
        formConfig: {
          configVersionId: schema.configVersionId,
          schemaHash: schema.schemaHash,
        },
      },
      files: draft.files,
      presentationManifest: createSubmissionPresentationManifest(schema.steps),
    }),
  });
  const body = await parseJson<{ submission?: { id: string; submittedAt: string }; error?: string }>(response);
  if (!response.ok || !body?.submission) throw new Error(body?.error ?? "제출에 실패했습니다.");
  return body.submission;
}

// 파일 업로드/삭제 경로의 버전 충돌(409) 응답 메시지 계약. 재동기화 후 1회 재시도 대상이다.
const PARTNER_FILE_CONFLICT_MESSAGES = new Set([
  "Draft version conflict",
  "Draft is no longer editable",
  "File not found or draft is no longer editable",
]);

export function isPartnerDraftVersionConflictError(error: unknown): boolean {
  return error instanceof Error && PARTNER_FILE_CONFLICT_MESSAGES.has(error.message);
}

export async function uploadPartnerFile(inviteId: string | undefined, draftVersion: number | undefined, file: File, category: FileCategory, fieldId: string): Promise<{ file: UploadedFile; draftVersion: number }> {
  if (!inviteId || draftVersion == null) throw new Error("파일 첨부를 위한 초대 링크 정보가 없습니다.");
  const formData = new FormData();
  formData.append("file", file);
  formData.append("category", category);
  formData.append("fieldId", fieldId);
  formData.append("draftVersion", String(draftVersion));
  const response = await fetch(`/api/invites/${encodeURIComponent(inviteId)}/files`, { method: "POST", body: formData });
  const body = await parseJson<{ file?: UploadedFile; draftVersion?: number; error?: string }>(response);
  if (!response.ok || !body?.file || body.draftVersion == null) throw new PartnerUploadError(response.status, body?.error ?? "파일 첨부에 실패했습니다.");
  return { file: body.file, draftVersion: body.draftVersion };
}

export async function deletePartnerFile(inviteId: string | undefined, draftVersion: number | undefined, file: UploadedFile): Promise<number> {
  if (!inviteId || draftVersion == null) throw new Error("파일 삭제를 위한 초대 링크 정보가 없습니다.");
  const response = await fetch(`/api/invites/${encodeURIComponent(inviteId)}/files/${encodeURIComponent(file.id)}?draftVersion=${draftVersion}`, { method: "DELETE" });
  const body = await parseJson<{ draftVersion?: number; error?: string }>(response);
  if (!response.ok || body?.draftVersion == null) throw new Error(body?.error ?? "파일 삭제에 실패했습니다.");
  return body.draftVersion;
}

export function getAdminPasswordHelperText(draft: DraftState, fallback?: string): string | undefined {
  if (!draft.hasSavedAdminPassword) return fallback;
  const currentPassword = fieldValueToString(draft.data.reservation.adminPassword).trim();
  return currentPassword ? fallback : "기존 비밀번호 유지";
}
